import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"
import { RootState } from "../store"
import { v4 } from "uuid"
import { EditSegmentModalProps } from "web/modals/EditSegmentModal"
import { ConfirmModalProps } from "web/modals/ConfirmModal"
import { EditEventModalProps } from "web/modals/EditEventModal"
import { TextAreaModalProps } from "web/modals/TextAreaModal"
import { InfoModalProps } from "web/modals/InfoModal"
import { EditPlayerModalProps } from "web/modals/EditPlayerModal"
import { PromptModalProps } from 'web/modals/PromptModal'
import { TabbedExportModalProps } from 'web/modals/tabbed-export-modal/TabbedExportModal'
import { CreateOutlineModalProps } from 'web/modals/CreateOutlineModal'
import { PruneOutlinesModalProps } from 'web/modals/PruneOutlinesModal'
import { SetupGistModalProps } from 'web/modals/SetupGistModal'

type ModalTypes =
    | "edit-segment"
    | "edit-event"
    | "edit-player"
    | "confirm"
    | "text-area"
    | "info"
    | "new-patch-info"
    | "import-fight"
    | "prompt"
    | "tabbed-export"
    | "create-outline"
    | "prune-outlines"
    | "setup-gist"

type OpenModal = {
    type: ModalTypes
    id: string
    options: any
}

export type ModalState = {
    modals: OpenModal[]
}

const initialState: ModalState = {
    modals: []
}

const slice = createSlice({
    name: "modal",
    initialState,
    reducers: {
        closeModal: (state, action: PayloadAction<{ modalId: string }>) => {
            state.modals = state.modals.filter(modal => modal.id !== action.payload.modalId)
        },

        openSetupGistModal: (state, action: PayloadAction<SetupGistModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "setup-gist",
            })
        },

        openPruneOutlinesModal: (state, action: PayloadAction<PruneOutlinesModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "prune-outlines",
            })
        },

        openCreateOutlineModal: (state, action: PayloadAction<CreateOutlineModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "create-outline",
            })
        },

        openTabbedExportModal: (state, action: PayloadAction<TabbedExportModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "tabbed-export",
            })
        },

        openPromptModal: (state, action: PayloadAction<PromptModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "prompt",
            })
        },

        openEditSegmentModal: (state, action: PayloadAction<EditSegmentModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "edit-segment",
            })
        },


        openEditEventModal: (state, action: PayloadAction<EditEventModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "edit-event",
            })
        },

        openEditPlayerModal: (state, action: PayloadAction<EditPlayerModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "edit-player",
            })
        },

        openTextAreaModal: (state, action: PayloadAction<TextAreaModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "text-area"
            })
        },

        openInfoModal: (state, action: PayloadAction<InfoModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "info"
            })
        },

        openConfirmModal: (state, action: PayloadAction<ConfirmModalProps>) => {
            state.modals.push({
                id: v4(),
                options: action.payload,
                type: "confirm"
            })
        },

        openNotifyAboutNewBuildModal: (state) => {
            state.modals.push({
                id: v4(),
                options: null,
                type: "new-patch-info"
            })
        },

        openImportFightModal: (state) => {
            state.modals.push({
                id: v4(),
                options: null,
                type: "import-fight"
            })
        },

    },
    extraReducers: {
        // Empty
    }
})

export const ModalActions = slice.actions
export const ModalReducer = slice.reducer

type ReturnHook = [
    typeof initialState,
    typeof slice.actions,
]

export function useModalState(): ReturnHook {
    const state = useSelector<RootState, ModalState>(state => state.modal)
    return [state, slice.actions]
}
